import type { TimelineElement } from '../../context/TimelineContext'

export const reducedMotionTransition = { duration: 0, type: false } as const

export const getLayoutTransition = (animationProfile: 'FLUID' | 'FAST' | 'REDUCED_MOTION') => {
  if (animationProfile === 'REDUCED_MOTION') return reducedMotionTransition
  if (animationProfile === 'FAST') return { type: 'spring', stiffness: 220, damping: 30 } as const
  return { type: 'spring', stiffness: 130, damping: 22 } as const
}

/**
 * Projects raw window client coordinates accurately into active canvas scaling vectors.
 */
export function projectScreenToCanvasCoordinates(
  clientX: number,
  clientY: number,
  boardBoundingRect: DOMRect,
  panX: number,
  panY: number,
  zoomScale: number,
) {
  return {
    x: (clientX - boardBoundingRect.left - panX) / zoomScale,
    y: (clientY - boardBoundingRect.top - panY) / zoomScale,
  }
}

export const splitMacroElements = (elements: TimelineElement[]) => {
  const goals = elements.filter((element) => element.type === 'GOAL')
  const tasks = elements.filter((element) => element.type === 'TASK')
  const subTasks = elements.filter((element) => element.type === 'SUBTASK')
  const tasksByGoalId = new Map<string, TimelineElement[]>()
  const subTasksByTaskId = new Map<string, TimelineElement[]>()

  for (const task of tasks) {
    if (!task.parentId) continue
    const siblings = tasksByGoalId.get(task.parentId) ?? []
    siblings.push(task)
    tasksByGoalId.set(task.parentId, siblings)
  }

  for (const subTask of subTasks) {
    if (!subTask.parentId) continue
    const siblings = subTasksByTaskId.get(subTask.parentId) ?? []
    siblings.push(subTask)
    subTasksByTaskId.set(subTask.parentId, siblings)
  }

  return { goals, tasks, subTasks, tasksByGoalId, subTasksByTaskId }
}

export const getProgressTone = (progress: number) => {
  if (progress >= 75) return 'bg-[var(--success)]'
  if (progress >= 40) return 'bg-[var(--info)]'
  if (progress >= 15) return 'bg-[var(--warning)]'
  return 'bg-[var(--danger)]'
}

export const getNodeTone = (index: number) =>
  [
    'bg-[var(--chart-1)]',
    'bg-[var(--chart-2)]',
    'bg-[var(--chart-3)]',
    'bg-[var(--chart-4)]',
    'bg-[var(--chart-5)]',
    'bg-[var(--accent)]',
  ][index % 6]

export const compactLabel = (label: string, maxLength = 28) =>
  label.length > maxLength ? `${label.slice(0, maxLength - 1)}...` : label

export const elementStartMs = (element: TimelineElement) => {
  const value = element.startDate ? new Date(element.startDate).getTime() : Number.NaN
  return Number.isFinite(value) ? value : 0
}

export const elementEndMs = (element: TimelineElement) => {
  const value = element.endDate ? new Date(element.endDate).getTime() : Number.NaN
  return Number.isFinite(value) ? value : elementStartMs(element)
}

export const formatElementRange = (element: TimelineElement) => {
  if (!element.startDate || !element.endDate) return 'No dates'
  const formatter = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${formatter.format(new Date(element.startDate))} - ${formatter.format(new Date(element.endDate))}`
}

export const getElementBounds = (elements: TimelineElement[]) => {
  const dated = elements.filter((element) => element.startDate && element.endDate)
  if (dated.length === 0) return null

  const startMs = Math.min(...dated.map(elementStartMs))
  const endMs = Math.max(...dated.map(elementEndMs))

  return {
    startMs,
    endMs: Math.max(startMs + 24 * 60 * 60 * 1000, endMs),
  }
}

export const getPaddedElementBounds = (elements: TimelineElement[]) => {
  const bounds = getElementBounds(elements)
  if (!bounds) return null

  const duration = Math.max(24 * 60 * 60 * 1000, bounds.endMs - bounds.startMs)
  const padding = duration * 0.08

  return {
    startMs: bounds.startMs - padding,
    endMs: bounds.endMs + padding,
  }
}

export const focusWindowForElement = (element: TimelineElement) => {
  const startMs = elementStartMs(element)
  const endMs = Math.max(startMs + 24 * 60 * 60 * 1000, elementEndMs(element))
  const itemDuration = endMs - startMs
  const windowDuration = itemDuration / 0.9
  const margin = windowDuration * 0.05

  return {
    startMs: startMs - margin,
    endMs: endMs + margin,
  }
}

export const getRangeStyle = (element: TimelineElement, startMs: number, endMs: number) => {
  const duration = Math.max(1, endMs - startMs)
  const itemStart = elementStartMs(element)
  const itemEnd = Math.max(itemStart + 24 * 60 * 60 * 1000, elementEndMs(element))
  const left = ((itemStart - startMs) / duration) * 100
  const width = ((itemEnd - itemStart) / duration) * 100

  return {
    left: `${left}%`,
    width: `${Math.min(100, Math.max(0.75, width))}%`,
  }
}

export const getElementColor = (element: TimelineElement, fallbackIndex = 0) =>
  element.color ??
  [
    'var(--chart-1)',
    'var(--chart-2)',
    'var(--chart-3)',
    'var(--chart-4)',
    'var(--chart-5)',
    'var(--accent)',
  ][fallbackIndex % 6]

export const getHoverTitle = (element: TimelineElement) =>
  [
    element.type,
    element.title,
    element.description,
    formatElementRange(element),
  ].filter(Boolean).join('\n')

export const buildScaleMarkers = (startMs: number, endMs: number, opts?: { zoom?: number }) => {
  const duration = Math.max(1, endMs - startMs)
  const zoom = Math.max(0.1, opts?.zoom ?? 1)
  const visibleDays = duration / (24 * 60 * 60 * 1000) / zoom
  const markers: { id: string; label: string; left: string }[] = []
  const maxMarkers = zoom >= 2 ? 20 : zoom < 0.5 ? 8 : 12

  const pushMarker = (date: Date, label: string) => {
    const leftValue = ((date.getTime() - startMs) / duration) * 100
    if (leftValue < 0 || leftValue > 100) return

    markers.push({
      id: `${label}-${date.getTime()}`,
      label,
      left: `${leftValue}%`,
    })
  }

  if (visibleDays <= 14) {
    const cursor = new Date(startMs)
    cursor.setHours(0, 0, 0, 0)
    if (cursor.getTime() < startMs) cursor.setDate(cursor.getDate() + 1)
    const formatter = new Intl.DateTimeFormat('en', { weekday: 'short', day: 'numeric' })
    while (cursor.getTime() <= endMs) {
      pushMarker(new Date(cursor), formatter.format(cursor))
      cursor.setDate(cursor.getDate() + 1)
    }
    return markers
  }

  if (visibleDays <= 75) {
    const cursor = new Date(startMs)
    cursor.setHours(0, 0, 0, 0)
    const day = cursor.getDay()
    const mondayOffset = day === 0 ? -6 : 1 - day
    cursor.setDate(cursor.getDate() + mondayOffset)
    if (cursor.getTime() < startMs) cursor.setDate(cursor.getDate() + 7)
    const formatter = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' })
    while (cursor.getTime() <= endMs) {
      pushMarker(new Date(cursor), formatter.format(cursor))
      cursor.setDate(cursor.getDate() + 7)
    }
    return markers
  }

  if (visibleDays <= 1200) {
    const cursor = new Date(startMs)
    cursor.setDate(1)
    cursor.setHours(0, 0, 0, 0)
    if (cursor.getTime() < startMs) cursor.setMonth(cursor.getMonth() + 1)
    const formatter = new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' })
    const stepMonths = Math.max(1, Math.ceil(visibleDays / 30 / maxMarkers))
    while (cursor.getTime() <= endMs) {
      pushMarker(new Date(cursor), formatter.format(cursor))
      cursor.setMonth(cursor.getMonth() + stepMonths)
    }
    return markers
  }

  const cursor = new Date(new Date(startMs).getFullYear(), 0, 1)
  if (cursor.getTime() < startMs) cursor.setFullYear(cursor.getFullYear() + 1)
  const stepYears = Math.max(1, Math.ceil(visibleDays / 365 / maxMarkers))
  while (cursor.getTime() <= endMs) {
    pushMarker(new Date(cursor), String(cursor.getFullYear()))
    cursor.setFullYear(cursor.getFullYear() + stepYears)
  }

  return markers
}
